import { useEffect, useState } from 'react';
import { ViewModeSwitch } from '@/components/ViewModeSwitch/ViewModeSwitch';
import appIcon from '@/assets/icons/app-icon.svg';
import { useLocation } from 'react-router-dom';
import { ALL_ROUTES } from '@/Router';
import { LanguageSwitcher } from '../LanguageSwitcher/LanguageSwitcher';
import { useTotemDetailView } from '@/hooks/useTotemDetailView';

const isDev = import.meta.env.DEV;

export function HeaderTotem() {
  const [isViewModeSwitchVisible, setIsViewModeSwitchVisible] = useState(false);
  const location = useLocation();
  const { isTotemDetailView } = useTotemDetailView();

  const isHome = location.pathname === ALL_ROUTES.basePath;

  const handleClickSwitchButton = () => {
    if (isDev) {
      setIsViewModeSwitchVisible(!isViewModeSwitchVisible);
    }
  };

  useEffect(() => {
    if (!isHome && isViewModeSwitchVisible) {
      setIsViewModeSwitchVisible(false);
    }
  }, [location, isHome, isViewModeSwitchVisible]);

  // Determine header background
  const headerClassName = `p-app flex flex-col mode-totem:px-0 ${
    isHome ? 'bg-white' : 
    isTotemDetailView ? '' : 'bg-background'

  } ${isTotemDetailView ? 'bg-linear-to-b from-black to-transparent' : ''}`;

  return (
    <header className={headerClassName}>
      {/* HOME: 3-column grid layout */}
      {isHome && <HomeHeader onLogoClick={handleClickSwitchButton} />}

      {/* NON-HOME PAGES: logo only — back button lives in TotemBottomNav */}
      {!isHome && (
        <div
          className="flex items-center justify-center h-t-bottom-nav px-[31px]"
          style={{ gridTemplateColumns: '1fr auto 1fr' }}
        >
          <LogoButton onClick={handleClickSwitchButton} />
        </div>
      )}

      {/* Horizontal line below header - Detail pages only */}
      {isTotemDetailView && <div className="w-full h-px bg-white" />}

      {/* View Mode Switch (dev only) */}
      <div
        className={`transition-all duration-300 overflow-hidden ${
          isViewModeSwitchVisible
            ? 'opacity-100 max-h-20'
            : 'opacity-0 max-h-0 pointer-events-none'
        }`}
      >
        <ViewModeSwitch />
      </div>
    </header>
  );
}

/**
 * Home header with centered logo and language switcher
 */
function HomeHeader({ onLogoClick }: { onLogoClick: () => void }) {
  return (
    <div
      className="mode-totem:grid mode-totem:items-center mode-totem:px-[31px] mode-totem:py-[45px]"
      style={{ gridTemplateColumns: '1fr auto 1fr' }}
    >
      <div>{/* Empty left column */}</div>

      <LogoButton onClick={onLogoClick} />

      <div className="mode-totem:flex mode-totem:justify-end">
        <LanguageSwitcher />
      </div>
    </div>
  );
}

/**
 * Logo button for view mode switch
 */
function LogoButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="logoButton flex items-center cursor-pointer transition-opacity hover:opacity-80 focus-visible:outline-2 focus-visible:outline-primary"
      aria-label="Toggle view mode settings"
      title="Click to show/hide view mode options"
    >
      <img
        src={appIcon}
        alt="Società Filologica Friulana"
        className="h-[105px] object-cover"
      />
    </button>
  );
}

